import { useEffect, useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { useLocale } from "../primitives/useLocale";
import {
  lockedThroughOf,
  nextSlotFor,
  pickAuthoredText,
  programCoverage,
  type AthleteTargets,
  type CurrentMax,
  type Exercise,
  type Program as ProgramT,
  type ProgramProgress as ProgramProgressT,
  type UnitsPreference,
  programRoundLabel,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";
import { IconButton } from "../primitives/IconButton";
import { Chip } from "../primitives/Chip";
import { spokenLabel } from "../Library/LibraryItemCardActions";
import type { PlanGapsLauncher } from "../Progress/planGaps";
import { ProgramPlanGaps } from "./ProgramPlanGaps";
import { useProgramPlanFit } from "./useProgramPlanFit";
import { ProgramPreview } from "./ProgramPreview";
import { makePlanStyles } from "./programsPlanStyles";
import { useRunPick } from "./useRunPick";
import { stateAria } from "../a11y";

// One FOLLOWED program's card in the "In corso" section. Compact hero
// (Antenore 2026-07-01): a medium title with a ⋯ overflow menu (View /
// Edit / Unfollow) instead of a row of tiny links; a one-line selected-slot
// subtitle instead of the big "Next up" card; week + day chips (wrap — a
// horizontal scroll would fight the carousel pager); and a compact Start on
// its OWN row, right-aligned, NOT full-width and NOT titled with the session
// (the day chip + subtitle already say which).
//
// No onStart (web, no logger): no chips, Start or selected line. NO delete
// here, only UNFOLLOW (deleting lives in My Programs, behind a confirm).
export function ProgramRunCard({
  program,
  progress,
  catalog,
  resolveName,
  unit,
  isStarting,
  onStart,
  onUnfollow,
  onEdit,
  readOnly = false,
  isCurrentBlock = false,
  onSetCurrentBlock,
  maxMeta,
  active = true,
  athleteTargets = null,
  onPlanGaps,
  theme,
  dark,
}: {
  program: ProgramT;
  progress: ProgramProgressT | null;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  /** Display unit for the preview's prescribed loads (stored kg). */
  unit: UnitsPreference;
  isStarting: boolean;
  /** Starts (weekIdx, sessionIdx) on the platform logger. Omit on hosts
   *  with no logger (web) — hides Start + the picker entirely. */
  onStart?: (weekIdx: number, sessionIdx: number) => void;
  onUnfollow: () => void;
  onEdit: () => void;
  /** ⭐ This program is FOLLOWED: it lives in its author's partition and the
   *  caller holds only a FOLLOW# pointer (next/coaching.md, "La SPEC del
   *  follower"). The pencil goes, because a PATCH there answers 404, and the
   *  ✕ stops meaning "archive my own row" and starts meaning "drop my
   *  pointer". The host decides what `onUnfollow` then writes. Default false
   *  ⇒ the card renders exactly as today. */
  readOnly?: boolean;
  /** AP-3: this program is the athlete's pinned "active block". Drives the
   *  star toggle; #393: it no longer feeds the targets. */
  isCurrentBlock?: boolean;
  /** Toggle this program as the active block (pin / un-pin). Omit to hide the
   *  star (e.g. the web mount, where the family doesn't pick a block). */
  onSetCurrentBlock?: () => void;
  /** The caller's MAXes, for the preview's bands (#358). */
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  /** False while the card sits on an off-screen carousel page: the open
   *  preview closes, so the pager does not keep the tallest page's height
   *  (#451). Default true ⇒ standalone uses are unchanged. */
  active?: boolean;
  /** #393: the athlete's weekly targets. The card says which muscle targets
   *  this program covers. Null or none ⇒ no coverage lines. */
  athleteTargets?: AthleteTargets | null;
  /** #492 F5: open today's draft on the week's gaps. Absent: no block. */
  onPlanGaps?: PlanGapsLauncher;
  theme: UiTheme;
  dark: boolean;
}) {
  const { t } = useTranslation();
  const lang = useLocale();
  // PE-21 — resolved ONCE: the name reaches a <Text>, an i18n
  // interpolation and an a11y label, and the last two accept any value
  // without complaint, so an object would have rendered as
  // "[object Object]" with nothing to catch it.
  const programName =
    pickAuthoredText(program.name, lang, program.authorLocale) ?? "";
  const next = useMemo(() => nextSlotFor(program, progress), [program, progress]);
  // One selection: the chips and the open preview's navigation (#358).
  const run = useRunPick(next);
  const { weekIdx, sessionIdx } = run;
  const [showPreview, setShowPreview] = useState(false);
  useEffect(() => {
    if (!active) setShowPreview(false);
  }, [active]);
  const styles = makePlanStyles(theme);
  const canStart = onStart !== undefined;
  const coverage = useMemo(
    () => (catalog ? programCoverage(program, catalog, athleteTargets) : null),
    [program, catalog, athleteTargets],
  );
  const fit = useProgramPlanFit(program, catalog);

  const selectedSession = program.sessions[sessionIdx];
  const selectedSessionLabel =
    pickAuthoredText(selectedSession?.name, lang, program.authorLocale) ??
    t("mobile.plan.sessionN", {
      n: sessionIdx + 1,
      defaultValue: "Session {{n}}",
    });

  const lastSession = progress && program.sessions[progress.lastSessionIdx];
  const lastSessionLabel =
    pickAuthoredText(
      lastSession ? lastSession.name : undefined,
      lang,
      program.authorLocale,
    ) ??
    (progress
      ? t("mobile.plan.sessionNShort", {
          n: progress.lastSessionIdx + 1,
          defaultValue: "S{{n}}",
        })
      : null);

  return (
    <View style={styles.cardRoot}>
      {/* Header: title + the "active block" star (AP-3). #393: the star only
          puts a program first; a favourite-style toggle, not an action verb,
          so it lives at the title level, not in the action row. */}
      <View style={styles.programHead}>
        <Text style={styles.programName} numberOfLines={2}>
          {programName}
        </Text>
        {onSetCurrentBlock ? (
          <IconButton
            theme={theme}
            name="star"
            active={isCurrentBlock}
            activeGlyph="fill-pr"
            onPress={onSetCurrentBlock}
            accessibilityLabel={
              isCurrentBlock
                ? t("mobile.plan.currentBlockOn", {
                    defaultValue: "Current block: tap to unpin",
                  })
                : t("mobile.plan.currentBlockOff", {
                    defaultValue: "Set as current block",
                  })
            }
            accessibilityHint={t("mobile.plan.currentBlockHint", {
              defaultValue: "Puts this program first",
            })}
          />
        ) : null}
      </View>

      {onPlanGaps ? (
        <ProgramPlanGaps
          program={program}
          catalog={catalog}
          onPlanGaps={onPlanGaps}
          theme={theme}
        />
      ) : null}

      {canStart ? (
        <>
          {/* Selected-slot subtitle — replaces the big "Next up" card. The day
              chips show only D-numbers, so the session name lives here. */}
          <Text style={styles.nextLine} numberOfLines={1}>
            {t("mobile.plan.programNextInline", {
              week: weekIdx + 1,
              session: selectedSessionLabel,
              defaultValue: "Next: W{{week}} · {{session}}",
            })}
          </Text>
          {progress && lastSessionLabel ? (
            <Text style={styles.programLast}>
              {t("mobile.plan.programLastCompleted", {
                week: progress.lastWeekIdx + 1,
                session: lastSessionLabel,
                defaultValue: `Last: W{{week}} · {{session}}`,
              })}
            </Text>
          ) : null}

          {/* Week chips; a single cycle keeps the SAME row with a plain line
              (HR-9b: the pager sizes every page to the tallest). WRAP, never a
              horizontal ScrollView: a pager inside a pager fights the swipe. (The
              preview heatmap scrolls; it wins the drag, see NestedHScrollView, #412.)
              With the preview open its navigation is the one selector (#358). */}
          {showPreview ? null : (
            <>
              <View style={styles.chipRow}>
                <Text style={styles.chipRowLabel}>
                  {t("mobile.plan.programWeekLabel", { defaultValue: "Week" })}
                </Text>
                {program.cycleWeeks > 1 ? (
                  <View style={styles.chipWrap}>
                    {Array.from({ length: program.cycleWeeks }, (_, i) => {
                      const active = i === weekIdx;
                      return (
                        <Chip
                          key={i}
                          theme={theme}
                          shape="box"
                          style={{ minWidth: 36 }}
                          selected={active}
                          onPress={() => run.pickWeek(i)}
                          label={t("mobile.plan.programWeekShort", {
                            n: i + 1,
                            defaultValue: programRoundLabel(i + 1),
                          })}
                        />
                      );
                    })}
                  </View>
                ) : (
                  // Same height as a chip row (chips are 36 boxes) so single-cycle
                  // and multi-week cards measure the same in the pager.
                  <View style={{ minHeight: 36, justifyContent: "center" }}>
                    <Text style={styles.programLast}>
                      {t("mobile.plan.singleCycle", {
                        defaultValue: "Single cycle, same every week",
                      })}
                    </Text>
                  </View>
                )}
              </View>

              {/* Day chips: one tap selects the session, ✓ on the active one (a11y). */}
              <View style={styles.chipRow}>
                <Text style={styles.chipRowLabel}>
                  {t("mobile.plan.programSessionLabel", { defaultValue: "Session" })}
                </Text>
                <View style={styles.chipWrap}>
                  {program.sessions.map((s, i) => {
                    const active = i === sessionIdx;
                    return (
                      <Chip
                        key={s.id || i}
                        theme={theme}
                        shape="box"
                        style={{ minWidth: 36 }}
                        selected={active}
                        accessibilityLabel={
                          pickAuthoredText(s.name, lang, program.authorLocale) ??
                          undefined
                        }
                        onPress={() => run.pickSession(i)}
                        label={t("mobile.plan.programDayShort", {
                          n: i + 1,
                          defaultValue: `D${i + 1}`,
                        })}
                      />
                    );
                  })}
                </View>
              </View>
            </>
          )}
        </>
      ) : null}

      {/* Actions — ONE row: the play Start (when runnable) aligned with View ·
          Edit · Unfollow (Antenore 2026-07-01: Start is a play icon on the same
          row as the others; no ⋯ menu). */}
      <View style={styles.actionRow}>
        {canStart ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("mobile.plan.programStartA11y", {
              session: selectedSessionLabel,
              defaultValue: "Start {{session}}",
            })}
            disabled={isStarting}
            onPress={() => onStart(weekIdx, sessionIdx)}
            style={({ pressed }) => [
              styles.startIconBtn,
              isStarting && styles.startButtonDisabled,
              pressed && styles.startButtonPressed,
            ]}
          >
            <Icon
              name="play"
              size={theme.typography.sizes.md}
              color={isStarting ? theme.palette.ink3 : theme.palette.inkOnBrand}
            />
          </Pressable>
        ) : null}
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: showPreview }}
          {...stateAria({ expanded: showPreview })}
          accessibilityLabel={
            showPreview
              ? t("mobile.plan.hide", { defaultValue: "Hide" })
              : t("mobile.plan.view", { defaultValue: "View" })
          }
          onPress={() => setShowPreview((v) => !v)}
          style={({ pressed }) => [styles.actionIconBtn, pressed && styles.chipPressed]}
        >
          <Icon name="list" size={theme.typography.sizes.md} color={theme.palette.ink2} />
        </Pressable>
        {readOnly ? null : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={spokenLabel(
              t("program.edit", { defaultValue: "Edit" }),
              programName,
            )}
            accessibilityHint={t("program.editing", { name: programName })}
            onPress={onEdit}
            style={({ pressed }) => [styles.actionIconBtn, pressed && styles.chipPressed]}
          >
            <Icon name="edit" size={theme.typography.sizes.md} color={theme.palette.ink2} />
          </Pressable>
        )}
        {/* Same ✕, two meanings. On a followed row it drops the caller's
            pointer and the author keeps the program, so the old hint ("it
            stays in My programs") would be false: the words swap with it. */}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={spokenLabel(
            readOnly
              ? t("library.unfollow", { defaultValue: "Stop following" })
              : t("program.unfollow", { defaultValue: "Unfollow" }),
            programName,
          )}
          accessibilityHint={
            readOnly
              ? t("library.unfollowHint", {
                  defaultValue:
                    "Remove it from your library. Its author keeps it.",
                })
              : t("mobile.plan.programUnfollowHint", {
                  defaultValue:
                    "Stop following this program (it stays in My programs)",
                })
          }
          onPress={onUnfollow}
          style={({ pressed }) => [styles.actionIconBtn, pressed && styles.chipPressed]}
        >
          <Icon name="x" size={theme.typography.sizes.md} color={theme.palette.ink3} />
        </Pressable>
      </View>

      {showPreview ? (
        <ProgramPreview
          program={program}
          catalog={catalog}
          resolveName={resolveName}
          unit={unit}
          theme={theme}
          dark={dark}
          maxMeta={maxMeta}
          lockedThrough={lockedThroughOf(program, progress)}
          start={run.start}
          onPick={run.pick}
          coverage={coverage}
          cycleBelowMev={fit ? fit.belowMev.map((g) => g.muscle) : undefined}
        />
      ) : null}
    </View>
  );
}
